import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import BaseButton from '../../components/common/BaseButton'
import MantineSelectBox from '../../components/common/MantineSelectBox'
import SignInInput from '../../components/common/SignInInput'
import { apiRequest } from '../../utils/api'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { formatPhoneNumber } from '../../utils/utils'
import { useAuthStore } from '../../store'
import { Icon } from '../../assets/icons'
import styles from '../../styles/user/UserSignUp.module.scss'

const UserSignUp = () => {
  const navigate = useNavigate()
  const [form, setForm] = useState({
    id: '',
    password: '',
    passwordConfirm: '',
    name: '',
    phone: '',
    email: '',
    birthdate: '',
    school: '',
    country: '',
    city: '',
    postalCode: '',
    address: '',
    referrerId: '',
  })
  const [idAvailable, setIdAvailable] = useState(null)
  const [countries, setCountries] = useState([])
  const signIn = useAuthStore((state) => state.signIn)

  useEffect(() => {
    fetchCountries()
  }, [])

  const fetchCountries = async () => {
    try {
      const res = await apiRequest('/getCountry', 'POST')
      if (res.data) {
        setCountries(res.data)
      }
    } catch (err) {
      console.error('국가 데이터 가져오기 실패:', err)
      notifications.show({
        title: 'Error',
        message: 'Failed to load national data',
        color: 'red',
      })
    }
  }

  const handleSelectCountry = (value) => {
    setForm((prev) => ({
      ...prev,
      country: value,
    }))
  }

  const handleNextButton = async () => {
    if (!form.id) return showAlertModal('Enter your ID')
    if (!idAvailable) return showAlertModal('Check for ID availability')
    if (!form.password) return showAlertModal('Enter your Password')
    if (!form.passwordConfirm)
      return showAlertModal('Enter your password confirmation')
    if (form.password !== form.passwordConfirm)
      return showAlertModal('Passwords do not match')
    if (!form.name) return showAlertModal('Enter your Name')
    if (!form.phone) return showAlertModal('> Enter your phone number ')
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return showAlertModal('Enter your email ')
    if (!form.birthdate) return showAlertModal('Enter your date of birth')
    if (!form.country) return showAlertModal(' Enter your country')
    if (!form.city) return showAlertModal('Enter your city')

    try {
      const res = await apiRequest('/register', 'POST', form)
      if (res.success) {
        const birthYear = Number(form.birthdate?.split('-')[0])
        const thisYear = new Date().getFullYear()
        const age = thisYear - birthYear

        const levelMap = {
          11: 991171,
          10: 991151,
          9: 991131,
        }
        const levelId = age >= 12 ? 991191 : (levelMap[age] ?? 991111)

        const newUser = {
          USER_ID: form.id,
          STATUS: 'O',
          ID: res.userKey,
          NAME: form.name,
          AVATAR: '',
          TEST: null,
          NICKNAME: '',
          STEP: 1,
          LEVEL_ID: levelId,
          CENTER_ID: null,
        }
        signIn(newUser, false)

        showAlertModal('Your account is ready!', 'success')
        navigate('/solver/individual', {
          state: {
            birth: form.birthdate,
            userType: 'general',
          },
        })
      }
    } catch (err) {
      console.error('업로드 데이터 가져오기 실패:', err)
      notifications.show({
        title: '오류',
        message: ' Registration unsuccessful',
        color: 'red',
      })
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value)
      setForm((prev) => ({ ...prev, [name]: formattedPhone }))
    } else {
      setForm((prev) => ({ ...prev, [name]: value }))
    }

    if (name === 'id') {
      setIdAvailable(null)
    }
  }

  const checkIdDuplicate = async () => {
    if (!form.id || form.id.trim() === '') {
      showAlertModal('Enter your ID')
      setIdAvailable(null)
      return
    }

    if (form.id.length < 3) {
      showAlertModal('ID must be at least 3 characters')
      setIdAvailable(null)
      return
    }

    try {
      const res = await apiRequest(`/checkId`, 'POST', { ID: form.id })
      console.log('res.available ', res)
      setIdAvailable(res.available)
    } catch (err) {
      console.error('ID 중복확인 실패:', err)
      notifications.show({
        title: 'Error',
        message: 'ID unavailable',
        color: 'red',
      })
      setIdAvailable(null)
    }
  }

  const showAlertModal = (message, type = 'error') => {
    const imgSrc =
      type === 'success'
        ? '/assets/smart-green-monster.png'
        : '/assets/rabbit-blue-monster-cry.png'
    const imgAlt = type === 'success' ? 'Success avatar' : 'Error avatar'

    modals.open({
      children: (
        <div className={styles.alertContainer}>
          <img src={imgSrc} alt={imgAlt} className={styles.rabbitMonster} />
          <p className={styles.alertText}>{message}</p>
          <BaseButton
            onClick={() => modals.closeAll()}
            className={styles.alertButton}
          >
            Confirm
          </BaseButton>
        </div>
      ),
      centered: true,
      withCloseButton: false,
    })
  }

  return (
    <div className={styles.pageWrapper}>
      {/* Background blobs */}
      <img src="/assets/login/background-5.svg" alt="" className={styles.topBg} />
      <img src="/assets/login/background-7.svg" alt="" className={styles.footerBg} />
      <img src="/assets/login/background-6.svg" alt="" className={styles.bottomRightBg} />
      <img src="/assets/login/background-8.svg" alt="" className={styles.bottomLeftBg} />

      {/* Deco elements */}
      <img src="/assets/login/deco-1.svg" alt="" className={styles.deco1} />
      <img src="/assets/login/deco-2.svg" alt="" className={styles.deco2} />
      <img src="/assets/login/deco-3.svg" alt="" className={styles.deco3} />
      <img src="/assets/login/deco-4.svg" alt="" className={styles.deco4} />
      <img src="/assets/login/deco-5.svg" alt="" className={styles.deco5} />
      <img src="/assets/login/deco-6.svg" alt="" className={styles.deco6} />

      {/* Header */}
      <header className={styles.pageHeader}>
        <Link to="/signin" className={styles.logoLink}>
          <img src="/assets/white-logo.svg" alt="Solution M" className={styles.logoImg} />
        </Link>
      </header>

      {/* Main content */}
      <div className={styles.mainContent}>
        <div className={styles.formCard}>
          <h1 className={styles.cardTitle}>Create Account</h1>
          <p className={styles.cardSubtitle}>
            Join Solution M and start your math adventure!
          </p>

          <div className={styles.formGrid}>
            {/* ID — full width (has inline Check button) */}
            <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
              <p className={styles.fieldLabel}>ID</p>
              <div className={styles.idRow}>
                <SignInInput
                  icon={<Icon name="email" color="#0D2A4F" size={18} />}
                  name="id"
                  value={form.id}
                  onChange={handleChange}
                  placeholder="Enter your ID"
                />
                <BaseButton
                  onClick={checkIdDuplicate}
                  className={styles.checkButton}
                >
                  Check
                </BaseButton>
              </div>
              {form.id && form.id.length < 3 && (
                <p className={styles.errorText}>
                  ID must be at least 3 characters
                </p>
              )}
              {idAvailable === false && (
                <p className={styles.errorText}>This ID is already in use</p>
              )}
              {idAvailable === true && (
                <p className={styles.successText}>This ID can be used</p>
              )}
            </div>

            {/* Password | Password Confirmation */}
            <div className={styles.inputGroup}>
              <p className={styles.fieldLabel}>Password</p>
              <SignInInput
                type="password"
                icon={<Icon name="lockOpen" color="#0D2A4F" size={20} />}
                isHide={true}
                name="password"
                value={form.password}
                onChange={handleChange}
                autoComplete="current-password"
                placeholder="Enter your password"
              />
            </div>
            <div className={styles.inputGroup}>
              <p className={styles.fieldLabel}>Password Confirmation</p>
              <SignInInput
                type="password"
                icon={<Icon name="lockOpen" color="#0D2A4F" size={20} />}
                isHide={true}
                name="passwordConfirm"
                value={form.passwordConfirm}
                onChange={handleChange}
                autoComplete="current-password"
                placeholder="Confirm your password"
              />
            </div>

            {/* Name | Phone */}
            <div className={styles.inputGroup}>
              <p className={styles.fieldLabel}>Name</p>
              <SignInInput
                icon={<Icon name="user" color="#0D2A4F" size={18} />}
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter your name"
              />
            </div>
            <div className={styles.inputGroup}>
              <p className={styles.fieldLabel}>Phone</p>
              <SignInInput
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="010-0000-0000"
              />
            </div>

            {/* Email | Date of Birth */}
            <div className={styles.inputGroup}>
              <p className={styles.fieldLabel}>Email</p>
              <SignInInput
                icon={<Icon name="email" color="#0D2A4F" size={18} />}
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter your email"
              />
            </div>
            <div className={styles.inputGroup}>
              <p className={styles.fieldLabel}>Date of Birth</p>
              <input
                type="date"
                className={styles.datePicker}
                name="birthdate"
                value={form.birthdate}
                onChange={handleChange}
              />
            </div>

            {/* Country | City */}
            <div className={styles.inputGroup}>
              <p className={styles.fieldLabel}>Country</p>
              <MantineSelectBox
                styles={{
                  input: {
                    backgroundColor: '#fafafa',
                    color: '#0d2a4f',
                    border: 'none',
                    borderRadius: '4px',
                    width: '100%',
                    height: '40px',
                    minHeight: '40px',
                    fontSize: '13px',
                    fontWeight: 500,
                    letterSpacing: '-0.65px',
                  },
                }}
                value={form.country}
                onChange={handleSelectCountry}
                data={countries}
                placeholder="Country"
                width="100%"
              />
            </div>
            <div className={styles.inputGroup}>
              <p className={styles.fieldLabel}>City</p>
              <SignInInput
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="Enter your city"
              />
            </div>

            {/* Referrer ID — full width, optional */}
            <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
              <p className={styles.fieldLabel}>
                Referrer ID{' '}
                <span style={{ fontSize: '12px', color: '#aaa', fontWeight: 400 }}>
                  (Optional)
                </span>
              </p>
              <SignInInput
                name="referrerId"
                value={form.referrerId}
                onChange={handleChange}
                placeholder="Enter referrer ID"
              />
            </div>

            {/* Sign Up button — full width */}
            <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.15 }}
              >
                <BaseButton
                  onClick={handleNextButton}
                  className={styles.signUpButton}
                  style={{ background: '#162543' }}
                >
                  Sign Up
                </BaseButton>
              </motion.div>
            </div>
          </div>

          {/* Already have an account */}
          <div className={styles.orDivider}>
            <div className={styles.dividerLine} />
            <span className={styles.orText}>or</span>
            <div className={styles.dividerLine} />
          </div>
          <p className={styles.signInLinkRow}>
            Already have an account?{' '}
            <Link to="/signin" className={styles.signInLinkText}>
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className={styles.pageFooter}>
        <div className={styles.footerLinks}>
          <span>Privacy Policy</span>
          <span className={styles.footerSeparator}>|</span>
          <span>Terms of Use</span>
          <span className={styles.footerSeparator}>|</span>
          <span>Help Center</span>
        </div>
        <p className={styles.copyright}>© 2024 Solution M. All rights reserved.</p>
      </footer>
    </div>
  )
}

export default UserSignUp
